import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { KeyRound, UserRound, Mail, Lock, Trash2, LogOut, Bell, ChevronRight, Plus, X, QrCode, Trophy, FileText, Newspaper, CalendarX } from 'lucide-react';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C, ListItem, AppBottomNav, MobileSheet} from './MobileShell';
import { enablePush } from './push';
import QRScanner from './QRScanner';

const inputCls = 'w-full rounded-lg px-4 py-3 text-sm outline-none';
const inputStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text };

function Sheet({ title, onClose, children }) {
  return (
    <MobileSheet className="fixed inset-0 z-50 flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-t-2xl p-5 pb-8 space-y-3 max-h-[85dvh] overflow-y-auto" style={{ background: C.card }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: C.dim }} /></button>
        </div>
        {children}
      </div>
    </MobileSheet>
  );
}

export default function MobileSettings({ initialSheet = null }) {
  const nav = useNavigate();
  const { me, setMe, logout, refreshMe, loading } = useMobile();
  const [sheet, setSheet] = useState(initialSheet);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);

  const onQRResult = (raw) => {
    setScanning(false);
    try {
      const data = JSON.parse(raw);
      if (data.code && data.pin) {
        setForm({ code: data.code, pin: String(data.pin) });
        toast.success('QR-Code erkannt — jetzt freischalten!');
        return;
      }
    } catch (_e) { /* kein JSON */ }
    toast.error('Kein gültiger Kennungs-QR-Code');
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const openSheet = (s) => { setForm({}); setSheet(s); };

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      setSheet(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Fehler');
    } finally { setBusy(false); }
  };

  const claim = () => run(async () => {
    const r = await mapi.post('/app/identities/claim', { code: form.code || '', pin: form.pin || '' });
    setMe(r.data);
  }, 'Kennung freigeschaltet!');

  const saveName = () => run(async () => {
    const r = await mapi.put('/app/profile', { full_name: form.full_name });
    setMe(r.data);
  }, 'Name geändert');

  const saveEmail = () => run(async () => {
    const r = await mapi.put('/app/profile', { email: form.email });
    setMe(r.data);
  }, 'E-Mail geändert');

  const savePassword = () => run(async () => {
    await mapi.put('/app/password', { current_password: form.current || '', new_password: form.next || '' });
  }, 'Passwort geändert');

  const deleteAccount = () => run(async () => {
    await mapi.post('/app/account/delete', { password: form.password || '' });
    logout();
  });

  const unlink = async (id) => {
    try {
      const r = await mapi.delete(`/app/identities/${id}`);
      setMe(r.data);
      toast.success('Kennung entfernt');
    } catch (e) { toast.error('Entfernen fehlgeschlagen'); }
  };

  const activatePush = () => run(async () => { await enablePush(); }, 'Push-Benachrichtigungen aktiviert!');

  const [prefs, setPrefs] = useState(null);
  useEffect(() => {
    mapi.get('/app/push/prefs').then((r) => setPrefs(r.data.prefs || {})).catch(() => setPrefs({}));
  }, []);
  const quizPushOn = prefs ? prefs.quiz_champion !== false : true;
  const docsPushOn = prefs ? prefs.documents !== false : true;
  const newsPushOn = prefs ? prefs.news !== false : true;
  const lessonsPushOn = prefs ? prefs.lessons !== false : true;
  const togglePref = async (key, current) => {
    const next = { messages: true, ...(prefs || {}), [key]: !current };
    setPrefs(next);
    try { await mapi.put('/app/push/prefs', { prefs: next }); }
    catch (_e) { toast.error('Speichern fehlgeschlagen'); }
  };
  const toggleQuizPush = () => togglePref('quiz_champion', quizPushOn);
  const toggleDocsPush = () => togglePref('documents', docsPushOn);
  const toggleNewsPush = () => togglePref('news', newsPushOn);
  const toggleLessonsPush = () => togglePref('lessons', lessonsPushOn);

  if (loading) return <MobileShell><div className="flex-1 flex items-center justify-center" style={{ color: C.dim }}>Laden…</div></MobileShell>;
  if (!me) { nav('/m/login'); return null; }

  return (
    <MobileShell bottomNav={<AppBottomNav />}>
      <MobileHeader title="Einstellungen" backTo="/m" />
      <div className="flex-1 overflow-y-auto pb-10">
        <p className="px-4 pt-4 pb-1 text-xs font-bold uppercase tracking-wider" style={{ color: C.red }}>Anwendung</p>
        <div className="mx-4 rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
          <ListItem icon={Bell} label="Push-Benachrichtigungen" sub="Browser oder Store-App — für Nachrichten aktivieren"
                    onClick={activatePush} testId="mobile-settings-push"
                    right={<ChevronRight className="w-4 h-4" style={{ color: C.dim }} />} />
          <ListItem icon={CalendarX} label="Unterricht: Ausfall & Verlegung" sub="Push wenn eine Stunde ausfällt oder verschoben wird"
                    onClick={toggleLessonsPush} testId="mobile-settings-lessons-push"
                    right={
                      <span className="w-10 h-6 rounded-full relative transition-colors shrink-0"
                            style={{ background: lessonsPushOn ? '#22c55e' : C.border }}
                            data-testid="mobile-settings-lessons-push-toggle"
                            data-state={lessonsPushOn ? 'on' : 'off'}>
                        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                              style={{ left: lessonsPushOn ? '18px' : '2px' }} />
                      </span>
                    } />
          <ListItem icon={Newspaper} label="Aktuelles" sub="Push bei neuen Meldungen der Musikschule"
                    onClick={toggleNewsPush} testId="mobile-settings-news-push"
                    right={
                      <span className="w-10 h-6 rounded-full relative transition-colors shrink-0"
                            style={{ background: newsPushOn ? '#22c55e' : C.border }}
                            data-testid="mobile-settings-news-push-toggle"
                            data-state={newsPushOn ? 'on' : 'off'}>
                        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                              style={{ left: newsPushOn ? '18px' : '2px' }} />
                      </span>
                    } />
          <ListItem icon={FileText} label="Neue Dokumente & Dateien" sub="Push bei Freigabe durch die Musikschule"
                    onClick={toggleDocsPush} testId="mobile-settings-docs-push"
                    right={
                      <span className="w-10 h-6 rounded-full relative transition-colors shrink-0"
                            style={{ background: docsPushOn ? '#22c55e' : C.border }}
                            data-testid="mobile-settings-docs-push-toggle"
                            data-state={docsPushOn ? 'on' : 'off'}>
                        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                              style={{ left: docsPushOn ? '18px' : '2px' }} />
                      </span>
                    } />
          <ListItem icon={Trophy} label="Quiz-Champion der Woche" sub="Wöchentlicher Push zur Bestenliste"
                    onClick={toggleQuizPush} testId="mobile-settings-quiz-push"
                    right={
                      <span className="w-10 h-6 rounded-full relative transition-colors shrink-0"
                            style={{ background: quizPushOn ? '#22c55e' : C.border }}
                            data-testid="mobile-settings-quiz-push-toggle"
                            data-state={quizPushOn ? 'on' : 'off'}>
                        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                              style={{ left: quizPushOn ? '18px' : '2px' }} />
                      </span>
                    } />
        </div>

        <p className="px-4 pt-5 pb-1 text-xs font-bold uppercase tracking-wider" style={{ color: C.red }}>Benutzer:in</p>
        <div className="mx-4 rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
          <ListItem icon={KeyRound} label="Kennungen verwalten" sub={`${me.identities.length} freigeschaltet`}
                    onClick={() => openSheet('identities')} testId="mobile-settings-identities"
                    right={<ChevronRight className="w-4 h-4" style={{ color: C.dim }} />} />
          <ListItem icon={UserRound} label="Namen ändern" sub={me.full_name}
                    onClick={() => { setForm({ full_name: me.full_name }); setSheet('name'); }} testId="mobile-settings-name" />
          <ListItem icon={Mail} label="E-Mail ändern" sub={me.email}
                    onClick={() => { setForm({ email: me.email }); setSheet('email'); }} testId="mobile-settings-email" />
          <ListItem icon={Lock} label="Passwort ändern"
                    onClick={() => openSheet('password')} testId="mobile-settings-password" />
          <ListItem icon={Trash2} label="Konto löschen" sub="DSGVO: alle App-Daten entfernen"
                    onClick={() => openSheet('delete')} testId="mobile-settings-delete" />
        </div>

        <p className="px-4 pt-5 pb-1 text-xs font-bold uppercase tracking-wider" style={{ color: C.red }}>Sitzung</p>
        <div className="mx-4 rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
          <ListItem icon={LogOut} label="Abmelden" onClick={logout} testId="mobile-settings-logout" />
        </div>
        <p className="text-center text-[11px] mt-8" style={{ color: C.dim }}>App-Version 1.0.0 (Phase 1)</p>
      </div>

      {sheet === 'identities' && (
        <Sheet title="Kennungen verwalten" onClose={() => setSheet(null)}>
          {me.identities.map((i) => (
            <div key={i.id} className="flex items-center justify-between rounded-lg px-3 py-2.5"
                 style={{ background: C.bg, border: `1px solid ${C.border}` }}
                 data-testid={`mobile-identity-${i.id}`}>
              <div>
                <p className="text-sm font-medium">{i.person_name}</p>
                <p className="text-[11px]" style={{ color: C.dim }}>
                  <span className="uppercase">
                    {{ student: 'Schüler', guardian: 'Eltern', teacher: 'Lehrer', admin: 'Lehrer' }[i.person_type] || i.person_type}
                  </span>
                  {i.tenant_name ? ` · ${i.tenant_name}` : ''}
                </p>
              </div>
              <button onClick={() => unlink(i.id)} data-testid={`mobile-identity-unlink-${i.id}`}>
                <Trash2 className="w-4 h-4" style={{ color: C.dim }} />
              </button>
            </div>
          ))}
          {!me.identities.length && <p className="text-sm" style={{ color: C.dim }}>Noch keine Kennung freigeschaltet.</p>}
          <button onClick={() => openSheet('claim')} data-testid="mobile-identity-add"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-white"
                  style={{ background: C.red }}>
            <Plus className="w-4 h-4" /> Kennung hinzufügen
          </button>
        </Sheet>
      )}

      {sheet === 'claim' && (
        <Sheet title="Kennung hinzufügen" onClose={() => setSheet(null)}>
          <p className="text-xs" style={{ color: C.dim }}>
            Gib die Kennung und PIN ein, die du von der Verwaltung erhalten hast —
            oder scanne einfach den QR-Code vom Zettel.
          </p>
          <button onClick={() => setScanning(true)} data-testid="mobile-claim-scan"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold"
                  style={{ background: C.bg, border: `1px solid ${C.red}`, color: C.text }}>
            <QrCode className="w-4 h-4" style={{ color: C.red }} /> QR-Code scannen
          </button>
          <input placeholder="Kennung (z. B. AB2C-3DEF)" value={form.code || ''} onChange={set('code')}
                 className={inputCls} style={inputStyle} data-testid="mobile-claim-code" />
          <input placeholder="PIN" inputMode="numeric" value={form.pin || ''} onChange={set('pin')}
                 className={inputCls} style={inputStyle} data-testid="mobile-claim-pin" />
          <button onClick={claim} disabled={busy} data-testid="mobile-claim-submit"
                  className="w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50" style={{ background: C.red }}>
            Freischalten
          </button>
        </Sheet>
      )}
      {scanning && <QRScanner onResult={onQRResult} onClose={() => setScanning(false)} />}

      {sheet === 'name' && (
        <Sheet title="Namen ändern" onClose={() => setSheet(null)}>
          <input value={form.full_name || ''} onChange={set('full_name')} className={inputCls} style={inputStyle} data-testid="mobile-name-input" />
          <button onClick={saveName} disabled={busy} className="w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50" style={{ background: C.red }} data-testid="mobile-name-save">Speichern</button>
        </Sheet>
      )}
      {sheet === 'email' && (
        <Sheet title="E-Mail ändern" onClose={() => setSheet(null)}>
          <input type="email" value={form.email || ''} onChange={set('email')} className={inputCls} style={inputStyle} data-testid="mobile-email-input" />
          <button onClick={saveEmail} disabled={busy} className="w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50" style={{ background: C.red }} data-testid="mobile-email-save">Speichern</button>
        </Sheet>
      )}
      {sheet === 'password' && (
        <Sheet title="Passwort ändern" onClose={() => setSheet(null)}>
          <input type="password" placeholder="Aktuelles Passwort" value={form.current || ''} onChange={set('current')} className={inputCls} style={inputStyle} data-testid="mobile-pw-current" />
          <input type="password" placeholder="Neues Passwort (min. 8 Zeichen)" value={form.next || ''} onChange={set('next')} className={inputCls} style={inputStyle} data-testid="mobile-pw-next" />
          <button onClick={savePassword} disabled={busy} className="w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50" style={{ background: C.red }} data-testid="mobile-pw-save">Speichern</button>
        </Sheet>
      )}
      {sheet === 'delete' && (
        <Sheet title="Konto löschen" onClose={() => setSheet(null)}>
          <p className="text-xs" style={{ color: C.dim }}>
            Dein Konto wird deaktiviert, alle Kennungen werden freigegeben und deine
            App-Daten (Push, Einstellungen) gelöscht. Dieser Schritt kann nicht rückgängig gemacht werden.
          </p>
          <input type="password" placeholder="Passwort zur Bestätigung" value={form.password || ''} onChange={set('password')} className={inputCls} style={inputStyle} data-testid="mobile-delete-password" />
          <button onClick={deleteAccount} disabled={busy} className="w-full py-3 rounded-lg font-semibold text-white disabled:opacity-50" style={{ background: '#7f1d1d' }} data-testid="mobile-delete-confirm">Konto endgültig löschen</button>
        </Sheet>
      )}
    </MobileShell>
  );
}
